import { Controller } from "@hotwired/stimulus"
import {
  createChart,
  createTextWatermark,
  CandlestickSeries,
  HistogramSeries,
  LineSeries
} from 'lightweight-charts'
import {
  calculateEMA,
  calculateSMA,
  buildChartOptions,
  buildCandlestickOptions,
  buildVolumeSeriesOptions,
  buildVolumeScaleOptions,
  buildVolumeData,
  buildWatermarkLines,
  buildMaSeriesOptions
} from '../utils/chart_utils'

// Controller for AI Chart Analysis page
// Handles chart rendering and VCP analysis.
//
// The ticker search box is NOT this controller's: it belongs to the shared
// `ticker-autocomplete` controller, which is mounted alongside this one and
// announces a pick as a `ticker-autocomplete:selected` event that
// `#tickerSelected` picks up. This file used to carry its own copy of the
// debounce / abort / stale-response / keyboard-nav machinery; the two drifted,
// and the shared one is the better of them (it builds its options with
// createElement rather than innerHTML, and carries the full combobox ARIA).
export default class extends Controller {
  static targets = [
    "chart", "chartTitle", "chartInfo", "typeLink",
    "scaleLinear", "scaleLog", "scaleIndicator",
    "analyzeButton", "loadingIndicator",
    "resultCard", "ratingBadge", "ratingDisplay", "ratingValue", "ratingLabel",
    "starRating", "ratingProgressBar",
    "summarySection", "summaryText",
    "criteriaSection", "criteriaList", "criteriaCount", "criteriaTitle",
    "recommendationSection", "recommendationText",
    "analysisContent", "analysisDescription", "tokenUsage", "inputTokens", "outputTokens", "costDisplay",
    "errorDisplay", "errorMessage", "analysisLoading",
    "usageCounter", "usageText", "usageResetNote"
  ]

  static values = {
    maConfig: { type: Array, default: [] },
    analysisType: { type: String, default: "vcp" },
    ticker: { type: String, default: "" },
    usage: { type: Number, default: 0 },
    limit: { type: Number, default: 10 },
    unlimited: { type: Boolean, default: false }
  }

  // Escape HTML special characters to prevent XSS
  escapeHtml(str) {
    const div = document.createElement('div')
    div.textContent = str
    return div.innerHTML
  }

  connect() {
    this.ticker = null
    this.logScale = false
    this.chart = null
    this.series = null
    this.volumeSeries = null
    this.maLines = []
    this.chartData = []
    this.chartRequestId = 0

    // Restore the chart when the page was loaded with a ticker (e.g. after
    // switching analysis type, which is a full navigation).
    if (this.tickerValue) {
      this.restoreTicker(this.tickerValue)
    }
  }

  disconnect() {
    if (this.chart) {
      this.chart.remove()
      this.chart = null
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
    // Invalidate any in-flight chart load so a late response can't touch
    // a torn-down chart target.
    this.chartRequestId++
  }

  // Re-select a ticker that came from the server (page load / navigation)
  // without re-triggering a search.
  // The search box's value is rendered server-side on this path, so there is
  // no input to populate here — only the chart to bring back.
  restoreTicker(ticker) {
    this.ticker = ticker
    this.setAnalyzeEnabled(true)
    this.loadChartData(ticker)
  }

  // True once the member has spent today's quota. Every path that would
  // enable Analyze goes through setAnalyzeEnabled, so a spent quota keeps
  // the button off rather than letting the member click into a refusal.
  quotaExhausted() {
    return !this.unlimitedValue && this.limitValue > 0 && this.usageValue >= this.limitValue
  }

  setAnalyzeEnabled(enabled) {
    if (!this.hasAnalyzeButtonTarget) return
    const exhausted = this.quotaExhausted()
    this.analyzeButtonTarget.disabled = !enabled || exhausted
    this.analyzeButtonTarget.title = exhausted ? "You've used today's AI analyses" : ''
  }

  // Entry point from the shared `ticker-autocomplete` controller, which owns
  // the search box on this page and announces a pick as
  // `ticker-autocomplete:selected`. It has already put the code in the field
  // and closed its dropdown; everything below is the part that is specific to
  // THIS page — load the chart, retitle it, keep the analysis-type links
  // pointing at the new ticker, and enable Analyze.
  tickerSelected(event) {
    const { ticker, name } = event.detail || {}
    if (!ticker) return

    this.selectTicker(ticker, name)
  }

  // Select a ticker and load its chart
  async selectTicker(ticker, name) {
    this.ticker = ticker
    this.tickerValue = ticker
    this.tickerName = name || ''

    // Update chart title (legend will be updated after chart renders)
    this.chartTitleTarget.textContent = `${ticker} - ${this.tickerName}`

    // Keep the analysis-type links pointing at the current ticker so
    // switching pattern type doesn't drop the chart.
    this.syncTypeLinks(ticker)

    // Enable analyze button (unless the quota is spent)
    this.setAnalyzeEnabled(true)

    // Clear previous analysis
    this.clearAnalysisResult()

    // Load chart data
    await this.loadChartData(ticker)
  }

  // Switch analysis type in place. The chart is identical for every pattern
  // type — only the prompt differs — so there is nothing to re-fetch. The
  // links keep working hrefs so middle/modified clicks and a cold load of the
  // URL still render the right page server-side.
  switchType(event) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    if (event.button != null && event.button !== 0) return

    event.preventDefault()

    const { type, name, description } = event.params
    if (!type || type === this.analysisTypeValue) return

    this.analysisTypeValue = type

    if (this.hasAnalysisDescriptionTarget && description) {
      this.analysisDescriptionTarget.textContent = description
    }

    this.highlightTypeLink(type)
    this.updateDocumentTitle(name)

    // The visible result belongs to the previous pattern — drop it rather
    // than leave it captioned under the new one.
    this.clearAnalysisResult()

    // Keep the address bar shareable without pushing a history entry the
    // back button would have to unwind.
    const href = event.currentTarget?.getAttribute('href')
    if (href && window.history?.replaceState) {
      window.history.replaceState(window.history.state, '', href)
    }
  }

  highlightTypeLink(type) {
    if (!this.hasTypeLinkTarget) return

    this.typeLinkTargets.forEach(link => {
      const active = link.dataset.aiChartTypeParam === type
      link.classList.toggle('btn-primary', active)
      link.classList.toggle('btn-outline-primary', !active)
      if (active) {
        link.setAttribute('aria-current', 'page')
      } else {
        link.removeAttribute('aria-current')
      }
    })
  }

  // Title is "<page> | <pattern> | <site>"; swap the middle segment only, and
  // leave it alone if the format ever changes.
  updateDocumentTitle(name) {
    if (!name) return

    const parts = document.title.split(' | ')
    if (parts.length < 2) return

    parts[1] = name
    document.title = parts.join(' | ')
  }

  syncTypeLinks(ticker) {
    if (!this.hasTypeLinkTarget) return

    this.typeLinkTargets.forEach(link => {
      try {
        const url = new URL(link.href, window.location.origin)
        if (ticker) {
          url.searchParams.set('ticker', ticker)
        } else {
          url.searchParams.delete('ticker')
        }
        link.href = url.pathname + url.search
      } catch (error) {
        console.warn('Could not update analysis type link:', error)
      }
    })
  }

  async loadChartData(ticker) {
    // Each load gets a token; only the newest one is allowed to touch the DOM.
    // Selecting a second ticker while the first is still loading previously
    // let the slower response win and render the wrong chart.
    const requestId = ++this.chartRequestId

    this.showLoading()

    try {
      const response = await fetch(`/ai/chart_data?ticker=${encodeURIComponent(ticker)}`, {
        headers: { 'Accept': 'application/json' }
      })
      const data = await response.json().catch(() => ({}))

      if (requestId !== this.chartRequestId) return

      if (data.error) {
        throw new Error(data.error)
      }
      if (!response.ok) {
        throw new Error(`Chart request failed (${response.status})`)
      }
      if (!Array.isArray(data.data) || data.data.length === 0) {
        throw new Error(`No price history available for ${ticker}`)
      }

      this.chartData = data.data
      this.stockInfo = data.stock

      // Prefer the server's name — it's authoritative and covers the
      // page-load restore path, where we only know the ticker.
      if (this.hasChartTitleTarget && data.stock && data.stock.ticker) {
        this.tickerName = data.stock.name || ''
        this.chartTitleTarget.textContent = `${data.stock.ticker} - ${this.tickerName}`
      }

      this.renderChart()
    } catch (error) {
      if (requestId !== this.chartRequestId) return

      console.error('Failed to load chart data:', error)
      this.teardownChart()
      this.chartTarget.innerHTML = `<div class="alert alert-danger m-3">Failed to load chart: ${this.escapeHtml(error.message)}</div>`
      this.syncAnalyzeAvailability()
    } finally {
      if (requestId === this.chartRequestId) {
        this.hideLoading()
      }
    }
  }

  // Analyze is only meaningful with a rendered chart to screenshot.
  syncAnalyzeAvailability() {
    this.setAnalyzeEnabled(Boolean(this.ticker && this.chart))
  }

  // Dispose of the chart and its observer. Called before every render and on
  // load failure so a dead chart object can never be screenshotted or resized.
  teardownChart() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
    if (this.chart) {
      try {
        this.chart.remove()
      } catch (error) {
        console.warn('Could not remove chart:', error)
      }
      this.chart = null
    }
    this.series = null
    this.volumeSeries = null
    this.maLines = []
  }

  renderChart() {
    // Clear existing chart (and its resize observer — leaking one per render
    // left stale observers firing against removed charts)
    this.teardownChart()
    this.chartTarget.innerHTML = ''

    // Clear any stale MA labels from previous chart
    const maLabelsSpan = this.chartInfoTarget?.querySelector('.ma-labels')
    if (maLabelsSpan) {
      maLabelsSpan.innerHTML = ''
    }

    const containerWidth = this.chartTarget.clientWidth || 800
    const containerHeight = this.chartTarget.clientHeight || 400

    const chartOptions = buildChartOptions({
      width: containerWidth,
      height: containerHeight,
      logScale: this.logScale,
      interactive: true,
      bottomMargin: 0.25
    })

    try {
      this.chart = createChart(this.chartTarget, chartOptions)

      // Add candlestick series
      this.series = this.chart.addSeries(CandlestickSeries, buildCandlestickOptions())
      this.series.setData(this.chartData)

      // Add volume series
      this.volumeSeries = this.chart.addSeries(HistogramSeries, buildVolumeSeriesOptions())
      this.chart.priceScale('volume').applyOptions(buildVolumeScaleOptions())
      this.volumeSeries.setData(buildVolumeData(this.chartData))

      // Add VCP moving averages (50, 150, 200 day)
      this.addMovingAverages()

      // Add watermark
      if (this.stockInfo) {
        this.addWatermark()
      }

      this.chart.timeScale().fitContent()
      this.updateScaleIndicator()

      // Add resize observer
      this.resizeObserver = new ResizeObserver(entries => {
        if (entries[0] && this.chart) {
          const { width, height } = entries[0].contentRect
          if (width > 0 && height > 0) {
            this.chart.applyOptions({ width, height })
          }
        }
      })
      this.resizeObserver.observe(this.chartTarget)
      this.syncAnalyzeAvailability()

    } catch (error) {
      console.error('Failed to render chart:', error)
      // Drop the half-built chart — leaving it assigned means a later
      // Analyze would screenshot a chart whose container no longer exists.
      this.teardownChart()
      this.chartTarget.innerHTML = `<div class="alert alert-danger m-3">Failed to render chart: ${this.escapeHtml(error.message)}</div>`
      this.syncAnalyzeAvailability()
    }
  }

  addMovingAverages() {
    // Use user's MA configuration from chart settings
    const maConfig = this.maConfigValue
    if (!maConfig || !maConfig.length) return

    this.maLines = []
    const labels = []

    maConfig.forEach(config => {
      if (!config.enabled || this.chartData.length < config.period) return

      const maData = config.type === 'ema'
        ? calculateEMA(this.chartData, config.period)
        : calculateSMA(this.chartData, config.period)

      const seriesOptions = buildMaSeriesOptions({
        period: config.period,
        color: config.color,
        lineWidth: parseFloat(config.line_width) || 1
      })

      const maSeries = this.chart.addSeries(LineSeries, seriesOptions)
      maSeries.setData(maData)
      this.maLines.push({ series: maSeries, config })

      // Build label for legend
      const bold = parseFloat(config.line_width) >= 1.5 ? 'font-weight: bold;' : ''
      labels.push(`<span style="color: ${config.color}; ${bold}">[${config.period}-day ${config.type.toUpperCase()}]</span>`)
    })

    this.updateChartLegend(labels)
  }

  // Update chart legend with MA labels
  updateChartLegend(labels) {
    if (!this.hasChartInfoTarget || !this.hasChartTitleTarget) return

    // Get the MA labels span, create if it doesn't exist
    let maLabelsSpan = this.chartInfoTarget.querySelector('.ma-labels')
    if (!maLabelsSpan) {
      maLabelsSpan = document.createElement('span')
      maLabelsSpan.className = 'ma-labels'
      this.chartInfoTarget.appendChild(maLabelsSpan)
    }
    maLabelsSpan.innerHTML = ` ${labels.join(' ')}`
  }

  // Expose calculateEMA for testing (delegates to shared utility)
  calculateEMA(data, period) {
    return calculateEMA(data, period)
  }

  addWatermark() {
    const lines = buildWatermarkLines(this.stockInfo)
    if (lines.length === 0) return

    try {
      const pane = this.chart.panes()[0]
      createTextWatermark(pane, {
        horzAlign: 'center',
        vertAlign: 'center',
        lines: lines
      })
    } catch (err) {
      console.warn('Could not create watermark:', err)
    }
  }

  toggleScale(event) {
    this.logScale = event.target.id === 'scaleLog'

    if (this.chart) {
      this.chart.priceScale('right').applyOptions({
        mode: this.logScale ? 1 : 0
      })
    }

    this.updateScaleIndicator()
  }

  updateScaleIndicator() {
    if (this.hasScaleIndicatorTarget) {
      this.scaleIndicatorTarget.textContent = this.logScale ? 'Log Scale' : 'Linear Scale'
    }
  }

  showLoading() {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.remove('d-none')
    }
  }

  hideLoading() {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.add('d-none')
    }
  }

  clearAnalysisResult() {
    if (this.hasResultCardTarget) {
      this.resultCardTarget.classList.add('d-none')
    }
    if (this.hasRatingBadgeTarget) {
      this.ratingBadgeTarget.classList.add('d-none')
    }
    if (this.hasRatingDisplayTarget) {
      this.ratingDisplayTarget.classList.add('d-none')
    }
    if (this.hasStarRatingTarget) {
      this.starRatingTarget.innerHTML = ''
    }
    if (this.hasSummarySectionTarget) {
      this.summarySectionTarget.classList.add('d-none')
    }
    if (this.hasCriteriaSectionTarget) {
      this.criteriaSectionTarget.classList.add('d-none')
    }
    if (this.hasRecommendationSectionTarget) {
      this.recommendationSectionTarget.classList.add('d-none')
    }
    if (this.hasAnalysisContentTarget) {
      this.analysisContentTarget.classList.add('d-none')
      this.analysisContentTarget.querySelector('.analysis-text').innerHTML = ''
    }
    if (this.hasTokenUsageTarget) {
      this.tokenUsageTarget.classList.add('d-none')
    }
    if (this.hasErrorDisplayTarget) {
      this.errorDisplayTarget.classList.add('d-none')
    }
  }

  // Capture the chart as a base64 PNG image.
  // Always captures at log scale — linear scale visually compresses moving averages
  // with small absolute gaps to the point the AI can't distinguish them.
  captureChartImage() {
    if (!this.chart) {
      throw new Error('No chart to capture')
    }

    const wasLogScale = this.logScale
    const priceScale = this.chart.priceScale('right')

    if (!wasLogScale) {
      priceScale.applyOptions({ mode: 1 })
    }

    try {
      const canvas = this.chart.takeScreenshot()
      return canvas.toDataURL('image/png').split(',')[1]
    } finally {
      if (!wasLogScale) {
        priceScale.applyOptions({ mode: 0 })
      }
    }
  }

  // Run VCP analysis
  async analyze() {
    if (!this.ticker) {
      return
    }

    // A failed/absent chart can't be analysed — say so instead of throwing
    // an opaque error out of captureChartImage.
    if (!this.chart) {
      this.clearAnalysisResult()
      this.resultCardTarget.classList.remove('d-none')
      this.displayError('Chart is not loaded yet — select a ticker and wait for the chart to appear.')
      return
    }

    // Disable button and show loading
    this.analyzeButtonTarget.disabled = true
    this.clearAnalysisResult()

    // Show result card with loading state
    this.resultCardTarget.classList.remove('d-none')
    this.analysisLoadingTarget.classList.remove('d-none')

    try {
      // Capture chart image from the already-rendered canvas
      const chartImage = await this.captureChartImage()

      const response = await fetch('/ai/analyze', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken()
        },
        body: JSON.stringify({
          ticker: this.ticker,
          chart_image: chartImage,
          analysis_type: this.analysisTypeValue
        })
      })

      // A session timeout or proxy error returns HTML, not JSON — don't let
      // the parse failure surface as an unexplained "Unexpected token <".
      const result = await response.json().catch(() => {
        throw new Error(
          response.status === 401 || response.status === 302
            ? 'Your session expired — please reload the page and sign in again.'
            : `Analysis failed (HTTP ${response.status})`
        )
      })

      // Update usage counter with response data
      if (result.usage) {
        this.updateUsageCounter(result.usage)
      }

      // Tier-paywall response (HTTP 403 + upgrade_required: true) — render
      // an inline upgrade CTA instead of the generic error display.
      if (response.status === 403 && result.upgrade_required) {
        this.displayUpgradePrompt(result.error || 'Upgrade required', result.upgrade_url)
      } else if (result.success) {
        this.displayAnalysisResult(result)
      } else {
        this.displayError(result.error || 'Analysis failed')
      }
    } catch (error) {
      console.error('Analysis failed:', error)
      this.displayError(error.message || 'Network error')
    } finally {
      this.analysisLoadingTarget.classList.add('d-none')
      this.setAnalyzeEnabled(true)
    }
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ''
  }

  displayAnalysisResult(result) {
    const rating = result.rating
    const ratingLabel = result.rating_label

    // Update rating badge in header
    this.ratingBadgeTarget.textContent = `${rating}/5 - ${ratingLabel}`
    this.ratingBadgeTarget.className = `badge ${this.getRatingBadgeClass(rating)}`

    // Update rating display with stars
    this.ratingDisplayTarget.classList.remove('d-none')
    this.ratingValueTarget.textContent = `${rating}/5`
    this.ratingLabelTarget.textContent = ratingLabel
    this.ratingLabelTarget.className = `rating-label text-uppercase fw-semibold mb-2 ${this.getRatingTextClass(rating)}`

    // Render star rating
    this.renderStarRating(rating)

    // Update progress bar
    if (this.hasRatingProgressBarTarget) {
      const percentage = (rating / 5) * 100
      this.ratingProgressBarTarget.style.width = `${percentage}%`
      this.ratingProgressBarTarget.className = `progress-bar ${this.getRatingProgressClass(rating)}`
    }

    // Check if we have structured data
    if (result.structured && result.criteria) {
      this.displayStructuredResult(result)
    } else {
      this.displayLegacyResult(result)
    }

    // Update token usage
    if (result.input_tokens && result.output_tokens) {
      this.tokenUsageTarget.classList.remove('d-none')
      this.inputTokensTarget.textContent = result.input_tokens.toLocaleString()
      this.outputTokensTarget.textContent = result.output_tokens.toLocaleString()
      this.costDisplayTarget.textContent = result.cost_cents ? `$${(result.cost_cents / 100).toFixed(2)}` : 'N/A'
    }
  }

  displayStructuredResult(result) {
    // Show summary
    if (result.summary && this.hasSummarySectionTarget) {
      this.summarySectionTarget.classList.remove('d-none')
      this.summaryTextTarget.textContent = result.summary
    }

    // Show criteria checklist
    if (result.criteria && this.hasCriteriaSectionTarget) {
      this.criteriaSectionTarget.classList.remove('d-none')
      this.criteriaCountTarget.textContent = `${result.criteria_met}/${result.criteria_total}`

      // Update criteria title based on analysis type
      if (this.hasCriteriaTitleTarget) {
        // Not "SEPA Criteria": Specific Entry Point Analysis is a registered
        // trademark, and this panel is screenshotted for the public
        // /features/ai-chart-analysis page. See docs/SEO.md (trademark rules).
        const titleMap = {
          'vcp': 'VCP Criteria',
          'bwave': 'Elliott Wave Criteria'
        }
        this.criteriaTitleTarget.textContent = titleMap[result.analysis_type] || 'Criteria'
      }

      // Use criteria labels from response
      this.renderCriteriaList(result.criteria, result.criteria_labels || {})
    }

    // Show recommendation
    if (result.recommendation && this.hasRecommendationSectionTarget) {
      this.recommendationSectionTarget.classList.remove('d-none')
      this.recommendationTextTarget.textContent = result.recommendation
    }

    // Hide legacy content
    if (this.hasAnalysisContentTarget) {
      this.analysisContentTarget.classList.add('d-none')
    }
  }

  displayLegacyResult(result) {
    // Hide structured sections
    if (this.hasSummarySectionTarget) this.summarySectionTarget.classList.add('d-none')
    if (this.hasCriteriaSectionTarget) this.criteriaSectionTarget.classList.add('d-none')
    if (this.hasRecommendationSectionTarget) this.recommendationSectionTarget.classList.add('d-none')

    // Show legacy markdown content
    this.analysisContentTarget.classList.remove('d-none')
    const contentDiv = this.analysisContentTarget.querySelector('.analysis-text')
    contentDiv.innerHTML = this.renderMarkdown(result.response)
  }

  renderStarRating(rating) {
    if (!this.hasStarRatingTarget) return

    const stars = []
    for (let i = 1; i <= 5; i++) {
      const filled = i <= rating
      const starClass = filled ? 'fas fa-star' : 'far fa-star'
      const colorClass = this.getStarColorClass(rating)
      stars.push(`<i class="${starClass} ${colorClass} star-icon" style="font-size: 1.5rem; margin: 0 2px;"></i>`)
    }
    this.starRatingTarget.innerHTML = stars.join('')

    // Add animation
    const starIcons = this.starRatingTarget.querySelectorAll('.star-icon')
    starIcons.forEach((star, index) => {
      star.style.opacity = '0'
      star.style.transform = 'scale(0)'
      setTimeout(() => {
        star.style.transition = 'all 0.2s ease-out'
        star.style.opacity = '1'
        star.style.transform = 'scale(1)'
      }, index * 100)
    })
  }

  renderCriteriaList(criteria, labels = {}) {
    if (!this.hasCriteriaListTarget) return

    const items = Object.entries(criteria).map(([key, value]) => {
      const met = value.met
      const details = value.details || ''
      // Use provided labels, fall back to formatting the key
      const label = this.escapeHtml(labels[key] || this.formatCriteriaKey(key))
      const icon = met ? 'fa-circle-check text-success' : 'fa-circle-xmark text-danger'
      const bgClass = met ? 'bg-success-subtle' : 'bg-danger-subtle'

      return `
        <div class="criteria-item d-flex align-items-start mb-2 p-2 rounded ${bgClass}">
          <i class="fas ${icon} me-2 mt-1" style="font-size: 1.1rem;"></i>
          <div class="flex-grow-1">
            <div class="fw-semibold">${label}</div>
            <div class="small text-muted">${this.escapeHtml(details)}</div>
          </div>
        </div>
      `
    })

    this.criteriaListTarget.innerHTML = items.join('')
  }

  // Format a snake_case criteria key into a readable label
  formatCriteriaKey(key) {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase())
  }

  getStarColorClass(rating) {
    if (rating >= 4) return 'text-success'
    if (rating >= 3) return 'text-warning'
    return 'text-danger'
  }

  getRatingTextClass(rating) {
    if (rating >= 4) return 'text-success'
    if (rating >= 3) return 'text-warning'
    return 'text-danger'
  }

  getRatingProgressClass(rating) {
    if (rating >= 4) return 'bg-success'
    if (rating >= 3) return 'bg-warning'
    return 'bg-danger'
  }

  displayError(message) {
    this.errorDisplayTarget.classList.remove('d-none')
    this.errorMessageTarget.textContent = message
  }

  // Render the daily-quota / tier-gated paywall in the existing error
  // display slot. The copy and the inline link distinguish it from a
  // hard error so the user understands they can resolve it by upgrading
  // rather than retrying.
  // A 403 with `upgrade_required: false` (a top-tier member out of quota)
  // takes the plain displayError path instead, so no link is offered.
  displayUpgradePrompt(message, upgradeUrl = '/subscriptions/pricing') {
    this.errorDisplayTarget.classList.remove('d-none')
    const link = document.createElement('a')
    link.href = upgradeUrl
    link.className = 'alert-link fw-bold ms-1'
    link.textContent = 'View plans →'
    this.errorMessageTarget.replaceChildren(document.createTextNode(`${message} `), link)
  }

  // Update the usage counter display after analysis
  updateUsageCounter(usage) {
    if (!usage || this.unlimitedValue) return

    this.usageValue = usage.used
    this.limitValue = usage.limit

    if (this.hasUsageTextTarget) {
      this.usageTextTarget.textContent = `${usage.used}/${usage.limit}`
      this.usageTextTarget.className = `usage-text ${this.getUsageColorClass(usage.used, usage.limit)}`
    }

    // The reset time is rendered server-side; only its visibility changes here.
    if (this.hasUsageResetNoteTarget) {
      this.usageResetNoteTarget.classList.toggle('d-none', !this.quotaExhausted())
    }
  }

  // Returns CSS class for usage counter based on remaining percentage
  getUsageColorClass(used, limit) {
    if (limit === 0) return 'text-success'

    const remainingPct = ((limit - used) / limit) * 100

    if (remainingPct > 50) return 'text-success'
    if (remainingPct > 25) return 'text-warning'
    if (remainingPct > 0) return 'text-orange'
    return 'text-danger'
  }

  getRatingBadgeClass(rating) {
    if (rating >= 4) return 'bg-success'
    if (rating >= 3) return 'bg-warning text-dark'
    return 'bg-danger'
  }

  // Simple markdown rendering with HTML escaping for safety
  renderMarkdown(text) {
    if (!text) return ''

    // Escape HTML first to prevent XSS, then apply markdown formatting
    let escaped = this.escapeHtml(text)

    // Convert markdown to HTML
    let html = escaped
      // Headers
      .replace(/^### (.+)$/gm, '<h5 class="mt-3 mb-2">$1</h5>')
      .replace(/^## (.+)$/gm, '<h4 class="mt-3 mb-2">$1</h4>')
      .replace(/^# (.+)$/gm, '<h3 class="mt-3 mb-2">$1</h3>')
      // Bold
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      // Italic
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      // Lists
      .replace(/^- (.+)$/gm, '<li>$1</li>')
      .replace(/^(\d+)\. (.+)$/gm, '<li>$2</li>')
      // Paragraphs
      .replace(/\n\n/g, '</p><p>')
      // Line breaks
      .replace(/\n/g, '<br>')

    // Wrap in paragraph
    html = `<p>${html}</p>`

    // Wrap lists
    html = html.replace(/(<li>.*<\/li>)/g, '<ul>$1</ul>')

    return html
  }
}
